import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { isOperator } from './auth/operator-scope';
import { CenteredPage, Shell } from './components/Shell';
import { useLocale, useOperatorScope, useSession, useT } from './hooks';
import { CallbackPage } from './pages/CallbackPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { NotOperatorPage } from './pages/NotOperatorPage';
import { ServicesContext, useServices, type OperatorServices } from './services';

/** Restores the session once (silent sign-in) on routes other than the OIDC callback. */
function useSessionBootstrap() {
  const { session } = useServices();
  const status = useSession().status;
  useEffect(() => {
    if (status === 'loading') void session.refresh();
  }, [session, status]);
  return status;
}

function Restoring() {
  const t = useT();
  return (
    <CenteredPage>
      <p role="status" aria-busy="true">
        {t('operator.login.restoring')}
      </p>
    </CenteredPage>
  );
}

/** Operator area: signed in with outlet_staff or dealer_staff (Req 3.1, 3.4). */
function RequireOperator({ children }: { children: (scope: NonNullable<ReturnType<typeof useOperatorScope>>) => ReactNode }) {
  const status = useSessionBootstrap();
  const session = useSession();
  const scope = useOperatorScope();
  const location = useLocation();
  if (status === 'loading') return <Restoring />;
  if (session.status !== 'signedIn') {
    const to = `${location.pathname}${location.search}`;
    return <Navigate to={to === '/' ? '/login' : `/login?to=${encodeURIComponent(to)}`} replace />;
  }
  if (!isOperator(session.claims)) return <NotOperatorPage />;
  return scope ? (
    <Shell claims={session.claims} scope={scope}>
      {children(scope)}
    </Shell>
  ) : null;
}

function LoginRoute() {
  const status = useSessionBootstrap();
  if (status === 'loading') return <Restoring />;
  return status === 'signedIn' ? <Navigate to="/" replace /> : <LoginPage />;
}

function OperatorApp() {
  const { connectivity } = useServices();
  const locale = useLocale();
  // Screen readers and font selection follow the UI language (WCAG 3.1.1).
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  useEffect(() => {
    connectivity.start();
    return () => connectivity.stop();
  }, [connectivity]);
  return (
    <Routes>
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="*" element={<RequireOperator>{(scope) => <HomePage scope={scope} />}</RequireOperator>} />
    </Routes>
  );
}

export function App({ services }: { services: OperatorServices }) {
  return (
    <ServicesContext.Provider value={services}>
      <BrowserRouter>
        <OperatorApp />
      </BrowserRouter>
    </ServicesContext.Provider>
  );
}
